//@ts-nocheck
import type * as CSS from 'csstype';

import type { Brand } from '../types/brand';
import { getThemeValue } from './getTheme';

export type ObjectOrArray<T, K extends keyof any = keyof any> = T[] | Record<K, T | Record<K, T> | T[]>;

export type Scale = ObjectOrArray<number | string>;

export type ThemeValue<K extends keyof ThemeType, ThemeType, TVal = any> = ThemeType[K] extends TVal[]
  ? number
  : ThemeType[K] extends Record<infer E, TVal>
    ? E
    : ThemeType[K] extends ObjectOrArray<infer F>
      ? F
      : never;

export interface ConfigStyle {
  /** The CSS property to use in the returned style object (overridden by `properties` if present). */
  property?: keyof CSS.Properties | undefined;
  /**
   * An array of multiple properties (e.g. `['marginLeft', 'marginRight']`) to which this style's value will be
   * assigned (overrides `property` when present).
   */
  properties?: Array<keyof CSS.Properties> | undefined;
  /** A string referencing a key in the `theme` object. */
  scale?: string | undefined;
  /** A fallback scale object for when there isn't one defined in the `theme` object. */
  defaultScale?: Scale | undefined;
  /** A function to transform the raw value based on the scale. */
  transform?: ((value: any, scale?: Scale) => any) | undefined;
}

export interface StyleFN {
  (...args: any[]): any;
  config?: object | undefined;
  propNames?: string[] | undefined;
  cache?: object | undefined;
}

export const merge = (a: { [x: string]: any }, b: { [x: string]: any }) => {
  const result = Object.assign({}, a, b);

  for (const key in a) {
    if (!a[key] || typeof b[key] !== 'object') continue;
    Object.assign(result, {
      [key]: Object.assign(a[key], b[key]),
    });
  }

  return result;
};

// sort object-value responsive styles
const sort = (obj: { [x: string]: any }) => {
  const next = {};
  Object.keys(obj)
    .sort((a, b) =>
      a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    )
    .forEach(key => {
      next[key] = obj[key];
    });

  return next;
};

const defaults = {
  breakpoints: [40, 52, 64].map(n => n + 'em'),
};

const createMediaQuery = (n: any) => `@media screen and (min-width: ${n})`;

export const createParser = (config: ConfigStyle): StyleFN => {
  const cache = {};

  const parse = (props: { [x: string]: any; theme: object | (string | number)[] }) => {
    let styles = {};
    let shouldSort = false;
    const isCacheDisabled = props.theme && props.theme.disableStyledSystemCache;

    for (const key in props) {
      if (!config[key]) continue;
      const sx = config[key];
      const raw = props[key];
      const scale = getThemeValue(props.theme, sx.scale, sx.defaults);

      if (typeof raw === 'object') {
        cache.breakpoints =
          (!isCacheDisabled && cache.breakpoints) || getThemeValue(props.theme, 'breakpoints', defaults.breakpoints);

        if (Array.isArray(raw)) {
          cache.media = (!isCacheDisabled && cache.media) || [null, ...cache.breakpoints.map(createMediaQuery)];
          styles = merge(styles, parseResponsiveStyle(cache.media, sx, scale, raw, props));
          continue;
        }

        if (raw !== null) {
          styles = merge(styles, parseResponsiveObject(cache.breakpoints, sx, scale, raw, props));
          shouldSort = true;
        }
        continue;
      }

      Object.assign(styles, sx(raw, scale, props));
    }

    // sort object-based responsive styles
    if (shouldSort) {
      styles = sort(styles);
    }

    return styles;
  };
  parse.config = config;
  parse.propNames = Object.keys(config);
  parse.cache = cache;

  const keys = Object.keys(config).filter(k => k !== 'config');

  if (keys.length > 1) {
    keys.forEach(key => {
      parse[key] = createParser({ [key]: config[key] });
    });
  }

  return parse;
};

const parseResponsiveStyle = (
  mediaQueries: string | any[],
  sx: (arg0: any, arg1: any, arg2: any) => any,
  scale: string | number | Brand | (string | number)[] | null | undefined,
  raw: any[],
  _props: any,
) => {
  const styles = {};
  raw.slice(0, mediaQueries.length).forEach((value: any, i: string | number) => {
    const media = mediaQueries[i];
    const style = sx(value, scale, _props);

    if (!media) {
      Object.assign(styles, style);
    } else {
      Object.assign(styles, {
        [media]: Object.assign({}, styles[media], style),
      });
    }
  });

  return styles;
};

const parseResponsiveObject = (
  breakpoints: { [x: string]: any },
  sx: (arg0: any, arg1: any, arg2: any) => any,
  scale: string | number | Brand | (string | number)[] | null | undefined,
  raw: { [x: string]: any },
  _props: any,
) => {
  const styles = {};

  for (const key in raw) {
    const breakpoint = breakpoints[key];
    const value = raw[key];
    const style = sx(value, scale, _props);

    if (!breakpoint) {
      Object.assign(styles, style);
    } else {
      const media = createMediaQuery(breakpoint);
      Object.assign(styles, {
        [media]: Object.assign({}, styles[media], style),
      });
    }
  }

  return styles;
};
